import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchTopics, fmtRelative, triggerRun } from "../lib/api";
import { useState } from "react";
import { toast } from "sonner";
import { Activity, BookOpen, Bookmark, Bot, Brain, Clock, CreditCard, Database, Eye, HeartPulse, History, LayoutDashboard, Link2, LogIn, LogOut, Mail, Shield, Sliders, Sparkles, Stamp, User as UserIcon, Users, Zap } from "lucide-react";
import WelcomePlaque from "./WelcomePlaque";
import SecurityMarker from "./SecurityMarker";
import { useAuth } from "../lib/auth";
import TopicTicker from "./TopicTicker";
import PricingPlaque from "./PricingPlaque";

export default function Layout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { data: topics = [] } = useQuery({
    queryKey: ["topics"],
    queryFn: fetchTopics,
    refetchInterval: 15000,
  });

  const [busy, setBusy] = useState(false);
  const onRunAll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await triggerRun({ target_count: 10 });
      toast.success("INGESTION QUEUED · ALL TOPICS", {
        description: "Topping up topics to 10/day. Refresh in ~30s.",
      });
    } catch (e) {
      toast.error("RUN FAILED", { description: e?.message || "unknown error" });
    } finally {
      setBusy(false);
    }
  };

  const navItems = [
    { to: "/dashboard", label: "OVERVIEW", icon: LayoutDashboard },
    { to: "/innovation", label: "INNOVATION", icon: Brain },
    { to: "/facts", label: "FACTS", icon: Sparkles },
    { to: "/watch", label: "WATCH", icon: Eye },
    { to: "/bot", label: "BOT", icon: Bot },
    { to: "/library", label: "RESEARCH_LIB", icon: Bookmark },
    { to: "/read-later", label: "SAVE_FOR_LATER", icon: Clock },
    { to: "/onestamp", label: "ONESTAMP", icon: Stamp },
    { to: "/email", label: "EMAIL", icon: Mail },
    { to: "/how-it-works", label: "HOW_IT_WORKS", icon: BookOpen },
    { to: "/wall", label: "WALL", icon: Sparkles },
    { to: "/teams", label: "TEAMS", icon: Users },
    { to: "/collab", label: "COLLAB", icon: Users },
    { to: "/security", label: "SECURITY", icon: Shield },
    { to: "/runs", label: "RUNS", icon: History },
    { to: "/sources", label: "SOURCES", icon: Link2 },
    { to: "/retention", label: "RETENTION", icon: Database },
    ...((user?.role === "admin" || user?.role === "owner")
      ? [
          { to: "/admin/ranking", label: "RANKING", icon: Sliders },
          { to: "/admin/health", label: "HEALTH", icon: HeartPulse },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0A] text-white">
      {/* live ticker — 10 topic libraries only (excludes innovation + fun facts) */}
      <TopicTicker />
      {/* top bar */}
      <header
        data-testid="app-header"
        className="border-b border-[#27272A] bg-[#0A0A0A] sticky top-0 z-30"
      >
        <div className="flex items-center px-4 h-12 gap-6">
          <Link
            to="/"
            data-testid="app-logo-link"
            className="flex items-center gap-2 font-mono"
          >
            <span className="inline-block w-2 h-2 bg-amber-400 animate-pulse" />
            <span className="text-base font-extrabold tracking-tight text-white">
              THE_MINDSTEIN
            </span>
            <span className="text-[10px] text-zinc-500 ml-2">v0.3</span>
          </Link>

          <nav className="flex items-center gap-1 ml-4">
            {navItems.map((n) => {
              const Icon = n.icon;
              const active =
                loc.pathname === n.to ||
                (n.to !== "/dashboard" && loc.pathname.startsWith(n.to));
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={
                    "px-3 h-8 flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider border " +
                    (active
                      ? "border-white bg-white text-black"
                      : "border-transparent text-zinc-400 hover:text-white hover:bg-[#141414]")
                  }
                >
                  <Icon size={12} />
                  {n.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {user ? (
              <>
                <SecurityMarker />
                <Link
                  to="/library"
                  data-testid="nav-library"
                  className="h-8 px-3 flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-purple-200 border border-purple-500/40 hover:bg-purple-500/10"
                >
                  <Bookmark size={12} /> MY_LIB
                </Link>
                <Link
                  to="/account"
                  data-testid="nav-account"
                  className="h-8 px-3 flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider border border-[#27272A] hover:bg-[#141414]"
                  title={user.email}
                >
                  <CreditCard size={12} className="text-amber-400" />
                  <span className="text-amber-300">{user.account?.plan?.name || user.plan}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-emerald-300 tabnum">{user.account?.total_remaining ?? 0}</span>
                </Link>
                <button
                  data-testid="logout-button"
                  onClick={async () => { nav("/", { replace: true }); await logout(); }}
                  className="h-8 px-3 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white border border-[#27272A] hover:border-rose-400/60 hover:bg-rose-500/10"
                  title="Sign out"
                >
                  <LogOut size={12} />
                  LOGOUT
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="h-8 px-3 flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider border border-[#27272A] hover:bg-[#141414]"
              >
                <LogIn size={12} /> SIGN_IN
              </Link>
            )}
            <button
              data-testid="run-all-button"
              onClick={onRunAll}
              disabled={busy}
              className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-white bg-white text-black hover:bg-zinc-200 disabled:opacity-50 flex items-center gap-2"
            >
              <Zap size={12} />
              {busy ? "QUEUEING…" : "RUN_ALL"}
            </button>
          </div>
        </div>
      </header>

      {/* main split */}
      <div className="flex flex-1">
        {/* sidebar */}
        <aside
          data-testid="topic-sidebar"
          className="w-64 border-r border-[#27272A] bg-[#0A0A0A] hidden md:block"
        >
          <div className="px-3 py-2 border-b border-[#27272A]">
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">
              TOPIC_LIBRARIES
            </span>
          </div>
          <ul>
            {topics.map((t) => {
              const pct = Math.min(100, (t.today_count / t.today_target) * 100);
              const filled = t.today_count >= t.today_target;
              return (
                <li key={t.id}>
                  <NavLink
                    to={`/topics/${t.slug}`}
                    data-testid={`topic-link-${t.slug}`}
                    className={({ isActive }) =>
                      "block px-3 py-2 border-b border-[#161616] hover:bg-[#111111] " +
                      (isActive ? "bg-[#141414]" : "")
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center min-w-0">
                        <span
                          className="dot"
                          style={{
                            background: filled ? "#10B981" : "#F59E0B",
                          }}
                        />
                        <span className="text-[12px] font-mono uppercase tracking-wide truncate">
                          {t.name}
                        </span>
                      </div>
                      <span
                        className={
                          "text-[10px] font-mono tabnum " +
                          (filled ? "text-emerald-400" : "text-amber-400")
                        }
                      >
                        {t.today_count}/{t.today_target}
                      </span>
                    </div>
                    <div className="mt-1 h-[2px] bg-[#1a1a1a]">
                      <div
                        className={filled ? "bg-emerald-500" : "bg-amber-500"}
                        style={{ width: `${pct}%`, height: "100%" }}
                      />
                    </div>
                    <div className="mt-1 text-[10px] text-zinc-500 font-mono">
                      {t.total_active} active · {fmtRelative(t.last_run_at)}
                    </div>
                  </NavLink>
                </li>
              );
            })}
          </ul>
          <div className="px-3 py-2 border-t border-[#27272A] mt-2">
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 font-mono">
              INNOVATION_SCOUT
            </span>
            <NavLink
              to="/innovation"
              data-testid="sidebar-innovation-link"
              className={({ isActive }) =>
                "mt-1 flex items-center gap-2 px-1 py-1 text-[11px] font-mono " +
                (isActive
                  ? "text-purple-200"
                  : "text-zinc-400 hover:text-white")
              }
            >
              <Brain size={12} className="text-purple-400" />
              MINDSTEIN_SCOUT · LIVE
            </NavLink>
          </div>
          {/* Phase 38 — PricingPlaque hidden during controlled early
              access. Restore this once live Quick Reply is open to the
              public. */}
        </aside>

        {/* content */}
        <main className="flex-1 min-w-0">{children}</main>
      </div>
      <WelcomePlaque />

      <footer className="border-t border-[#27272A] px-4 py-2 flex items-center gap-4 text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
        <span className="flex items-center gap-2">
          <Activity size={10} className="text-emerald-500" />
          SCHEDULER · LIVE
        </span>
        <span>RETENTION · 30D</span>
        <span>INNOV_CAP · 300</span>
        <span>MODEL · CLAUDE_SONNET_4.5</span>
        <span className="ml-auto">THE_MINDSTEIN · 2026</span>
      </footer>
    </div>
  );
}
